import { onMounted } from 'vue';
import landingSeoContent from '@jaaluu/data/landingSeoContent.json';
import landingSeoSpokes from '@jaaluu/data/landingSeoSpokes.json';

// The 9 languages Jaaluu's landing pages know about, both as a UI language
// and as a spoke's learning target. `zh` has no structured course content
// (see useLanguagePairFacts.ts's COURSE_LANGS, which deliberately excludes
// it), but it does have real feed content and is a selectable `langForeign`
// in the app - so it's a valid spoke targetLang like any other, just one
// where languageProfiles.json/languagePairFacts.json aren't authored yet
// (getLanguagePairFacts() returns null for it until they are, same as any
// other not-yet-authored pair).
export type CourseLang = 'de' | 'en' | 'es' | 'fr' | 'it' | 'pt' | 'ru' | 'pl' | 'zh';
export type LandingLang = CourseLang;

export type LandingGroup =
  | 'language-newspaper'
  | 'ai-chat-teacher'
  | 'digital-dictionary'
  | 'vocabulary-trainer'
  | 'natural-method'
  | 'reading-practice'
  | 'live-transcription'
  | 'video-object-detection'
  | 'learn-language'
  | 'read-text';

// Keep in sync with the routes declared in @jaaluu/router
export const LANDING_GROUPS: LandingGroup[] = [
  'language-newspaper',
  'ai-chat-teacher',
  'digital-dictionary',
  'vocabulary-trainer',
  'natural-method',
  'reading-practice',
  'live-transcription',
  'video-object-detection',
  'learn-language',
  'read-text'
];

type LandingSeoEntry = {
  path: string;
  navTitle: string;
  title: string;
  description: string;
  h1: string;
};
type LandingSeoContent = Record<LandingGroup, Partial<Record<LandingLang, LandingSeoEntry>>>;

// Single source of truth for each landing page's path/navTitle/title/description,
// also consumed verbatim (as plain JSON, no build step) by
// scripts/prerender-landing-meta.mjs to bake per-route <title>/<meta description>
// and static cross-links into static HTML at build time.
const SEO_CONTENT = landingSeoContent as LandingSeoContent;

// Keep in sync with the routes declared in @jaaluu/router
export const LANDING_PAGE_PATHS: Record<LandingGroup, Partial<Record<LandingLang, string>>> =
  Object.fromEntries(
    Object.entries(SEO_CONTENT).map(([group, langs]) => [
      group,
      Object.fromEntries(Object.entries(langs).map(([lang, entry]) => [lang, entry!.path]))
    ])
  ) as Record<LandingGroup, Partial<Record<LandingLang, string>>>;

// Short nav-label text per group/language, used to link between sibling landing
// pages (see LandingCrossLinks.vue and scripts/prerender-landing-meta.mjs).
// Sourced from landingSeoContent.json's "navTitle" field, kept separate from
// the long SEO titles.
export const LANDING_GROUP_TITLES: Record<LandingGroup, Partial<Record<LandingLang, string>>> =
  Object.fromEntries(
    Object.entries(SEO_CONTENT).map(([group, langs]) => [
      group,
      Object.fromEntries(Object.entries(langs).map(([lang, entry]) => [lang, entry!.navTitle]))
    ])
  ) as Record<LandingGroup, Partial<Record<LandingLang, string>>>;

const SEO_TAG_ATTR = 'data-landing-seo';

export const urlFor = (lang: string, path: string) => `https://${lang}.jaaluu.com/${path}`;

// og:locale wants an underscore locale tag, not a bare language code.
export const OG_LOCALE: Record<LandingLang, string> = {
  de: 'de_DE',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR',
  it: 'it_IT',
  pl: 'pl_PL',
  pt: 'pt_PT',
  ru: 'ru_RU',
  zh: 'zh_CN'
};

export const setMetaDescription = (description: string) => {
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    metaDesc.setAttribute('content', description);
  }
};

export const setCanonical = (href: string) => {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="canonical"][${SEO_TAG_ATTR}]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export const setHreflangAlternates = (alternates: { hreflang: string; href: string }[]) => {
  document.querySelectorAll(`link[rel="alternate"][${SEO_TAG_ATTR}]`).forEach((el) => el.remove());
  alternates.forEach(({ hreflang, href }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    link.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(link);
  });
};

// Reused by every setOgTags() call - the tags exist by default in index.html
// (as the site-wide fallback for pages that don't call setOgTags), so this
// updates them in place rather than creating duplicates.
const setOgTag = (property: string, content: string) => {
  let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    meta.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Twitter/X card tags use name="..." rather than property="...", so they need
// their own lookup/creation, but otherwise follow the same update-in-place
// pattern as setOgTag() (tags default in index.html for the site-wide fallback).
const setTwitterTag = (name: string, content: string) => {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    meta.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

export const setOgTags = (entry: {
  title: string;
  description: string;
  url: string;
  image: string;
  locale: string;
}) => {
  setOgTag('og:type', 'website');
  setOgTag('og:site_name', 'Jaaluu');
  setOgTag('og:title', entry.title);
  setOgTag('og:description', entry.description);
  setOgTag('og:url', entry.url);
  setOgTag('og:image', entry.image);
  setOgTag('og:locale', entry.locale);

  // X (Twitter) cards - mirrors the og:* values above rather than taking its
  // own params, since every current caller wants the same title/description/image.
  setTwitterTag('twitter:card', 'summary_large_image');
  setTwitterTag('twitter:title', entry.title);
  setTwitterTag('twitter:description', entry.description);
  setTwitterTag('twitter:image', entry.image);
};

// Since this is a single-page app, the <head> persists across client-side
// navigation - a tag set on one route stays put until something overwrites
// or removes it. So every route that should be indexable must explicitly set
// "index, follow" (not just skip setting anything), or it can inherit a
// leftover "noindex" from whichever app page the user was on before.
export const setRobotsMeta = (content: string) => {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name="robots"][${SEO_TAG_ATTR}]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Same Organization/SoftwareApplication/BreadcrumbList graph
// scripts/prerender-landing-meta.mjs bakes into the static HTML - no FAQPage,
// since landingSeoContent.json's "intro.items" are feature highlights, not
// literal question/answer pairs. Needed client-side too: SPA navigation
// between landing pages (e.g. via LandingCrossLinks) doesn't reload the page,
// so without this a route would keep whichever page's JSON-LD was set first.
export const setJsonLd = (graph: Record<string, unknown>[]) => {
  let script = document.querySelector<HTMLScriptElement>(
    `script[type="application/ld+json"][${SEO_TAG_ATTR}]`
  );
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute(SEO_TAG_ATTR, '');
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
};

// Sets title/description (from landingSeoContent.json) plus canonical/hreflang
// tags on mount. scripts/prerender-landing-meta.mjs bakes the same values into
// the static per-route HTML at build time, for crawlers that don't run JS.
export const useLandingSeo = (group: LandingGroup, lang: LandingLang) => {
  onMounted(() => {
    const paths = LANDING_PAGE_PATHS[group];
    const entry = SEO_CONTENT[group][lang];
    const canonicalPath = paths[lang];
    if (!entry || !canonicalPath) return;

    document.title = entry.title;
    setMetaDescription(entry.description);
    setRobotsMeta('index, follow');

    const canonicalHref = urlFor(lang, canonicalPath);
    setCanonical(canonicalHref);
    setOgTags({
      title: entry.title,
      description: entry.description,
      url: canonicalHref,
      image: urlFor(lang, 'og-image.png'),
      locale: OG_LOCALE[lang]
    });
    setJsonLd([
      {
        '@type': 'Organization',
        '@id': 'https://jaaluu.com/#organization',
        name: 'Jaaluu',
        url: 'https://jaaluu.com'
      },
      {
        '@type': 'SoftwareApplication',
        name: entry.navTitle,
        description: entry.description,
        url: canonicalHref,
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Jaaluu', item: urlFor(lang, 'welcome') },
          { '@type': 'ListItem', position: 2, name: entry.navTitle, item: canonicalHref }
        ]
      }
    ]);

    const alternates = Object.entries(paths).map(([altLang, altPath]) => ({
      hreflang: altLang,
      href: urlFor(altLang, altPath as string)
    }));
    if (paths.en) {
      alternates.push({ hreflang: 'x-default', href: urlFor('en', paths.en) });
    }
    setHreflangAlternates(alternates);
  });
};

// --- Spoke pages (topic x native-UI-language x target-language) -----------
//
// Spokes add a third axis on top of the hub pages above: which foreign
// language the visitor wants to learn (e.g. "sprachzeitung-englisch" is the
// language-newspaper topic, German UI, targeting English). See
// C:\Users\mimeie\.claude\plans\gleaming-spinning-kernighan.md for the full
// rollout - only a pilot slice of spokes.json is populated so far.
export type SpokeFaq = {
  heading: string;
  items: { q: string; a: string }[];
};
export type SpokeSeoEntry = {
  path: string;
  navTitle: string;
  title: string;
  description: string;
  h1: string;
  // Optional per-pair Q&A block, authored only for spokes whose keyword
  // cluster is question-shaped (e.g. de->es "Nachrichten auf Spanisch
  // lesen/hören/mit Übersetzung"). Rendered on-page by FaqSection.vue, baked
  // into the static HTML by scripts/prerender-landing-meta.mjs, and emitted as
  // FAQPage JSON-LD by both sides - so unlike the hub pages' "intro.items"
  // (feature highlights, not questions) these are real question/answer pairs.
  faq?: SpokeFaq;
};
type LandingSeoSpokes = Record<
  LandingGroup,
  Partial<Record<LandingLang, Partial<Record<CourseLang, SpokeSeoEntry>>>>
>;
const SPOKE_CONTENT = landingSeoSpokes as unknown as LandingSeoSpokes;

export const getSpokeEntry = (
  group: LandingGroup,
  uiLang: LandingLang,
  targetLang: CourseLang
): SpokeSeoEntry | undefined => SPOKE_CONTENT[group]?.[uiLang]?.[targetLang];

// Every (uiLang, targetLang) pair the given topic currently has spoke content
// for, in a given UI language - used to render the sibling-target-language
// switcher on a spoke page (LandingCrossLinks-equivalent for spokes).
export const spokeTargetsForUiLang = (
  group: LandingGroup,
  uiLang: LandingLang
): Partial<Record<CourseLang, SpokeSeoEntry>> => SPOKE_CONTENT[group]?.[uiLang] ?? {};

// The hreflang cluster for a spoke is NOT the hub's flat 9-member set - a UI
// language never appears in its own target's cluster (there's no "German
// speakers learning German" page), so it has at most 8 members (the other 8
// landing languages minus targetLang itself), and fewer while content is
// still being authored. Derived from SPOKE_CONTENT rather than
// hand-maintained, same pattern as LANDING_PAGE_PATHS above.
export const spokeClusterPaths = (
  group: LandingGroup,
  targetLang: CourseLang
): Partial<Record<LandingLang, string>> => {
  const cluster: Partial<Record<LandingLang, string>> = {};
  const uiLangs = SPOKE_CONTENT[group] ?? {};
  for (const uiLang of Object.keys(uiLangs) as LandingLang[]) {
    const entry = uiLangs[uiLang]?.[targetLang];
    if (entry) cluster[uiLang] = entry.path;
  }
  return cluster;
};

// Sets title/description/canonical/hreflang/JSON-LD for a spoke page, mirroring
// useLandingSeo() above but reading SPOKE_CONTENT and using the target-scoped
// hreflang cluster instead of the hub's flat one. Hub and spoke pages are
// never hreflang-linked to each other - different search intent even within
// the same UI language - only plain <a> cross-links connect them (see
// LandingCrossLinks.vue / SpokeCrossLinks.vue).
// The visible breadcrumb (BreadcrumbTrail.vue) and the static prerender both
// need this same Jaaluu -> hub -> spoke chain, so it's derived once here
// rather than duplicated - the hub level is omitted (falling back to the
// 2-level Jaaluu -> spoke chain) only if this topic has no hub page for
// uiLang, which shouldn't happen for an authored spoke but isn't guaranteed
// by the types.
export const spokeBreadcrumbTrail = (
  group: LandingGroup,
  uiLang: LandingLang,
  entry: SpokeSeoEntry
): { name: string; href: string }[] => {
  const trail = [{ name: 'Jaaluu', href: urlFor(uiLang, 'welcome') }];
  const hubPath = LANDING_PAGE_PATHS[group]?.[uiLang];
  const hubTitle = LANDING_GROUP_TITLES[group]?.[uiLang];
  if (hubPath && hubTitle) {
    trail.push({ name: hubTitle, href: urlFor(uiLang, hubPath) });
  }
  trail.push({ name: entry.navTitle, href: urlFor(uiLang, entry.path) });
  return trail;
};

export const useSpokeLandingSeo = (group: LandingGroup, uiLang: LandingLang, targetLang: CourseLang) => {
  onMounted(() => {
    const entry = getSpokeEntry(group, uiLang, targetLang);
    if (!entry) return;

    document.title = entry.title;
    setMetaDescription(entry.description);
    setRobotsMeta('index, follow');

    const canonicalHref = urlFor(uiLang, entry.path);
    setCanonical(canonicalHref);
    setOgTags({
      title: entry.title,
      description: entry.description,
      url: canonicalHref,
      image: urlFor(uiLang, 'og-image.png'),
      locale: OG_LOCALE[uiLang]
    });
    const graph: Record<string, unknown>[] = [
      {
        '@type': 'Organization',
        '@id': 'https://jaaluu.com/#organization',
        name: 'Jaaluu',
        url: 'https://jaaluu.com'
      },
      {
        '@type': 'SoftwareApplication',
        name: entry.navTitle,
        description: entry.description,
        url: canonicalHref,
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: spokeBreadcrumbTrail(group, uiLang, entry).map(({ name, href }, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name,
          item: href
        }))
      }
    ];
    // Only for spokes that actually render a visible FAQ section - Google
    // drops (and can penalize) FAQPage markup whose Q&As aren't on the page.
    if (entry.faq?.items.length) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: entry.faq.items.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a }
        }))
      });
    }
    setJsonLd(graph);

    const cluster = spokeClusterPaths(group, targetLang);
    const alternates = Object.entries(cluster).map(([altLang, altPath]) => ({
      hreflang: altLang,
      href: urlFor(altLang, altPath as string)
    }));
    // Omit x-default rather than invent a fallback when there's no en-UI
    // member - either structurally (targetLang === 'en', so no "English
    // speakers learn English" page can exist) or because that variant just
    // hasn't been authored yet.
    if (cluster.en) {
      alternates.push({ hreflang: 'x-default', href: urlFor('en', cluster.en) });
    }
    setHreflangAlternates(alternates);
  });
};
